import React, { useEffect, useState } from 'react';
import { getAssetDoc } from '../data/firestore';
import { clipSpeed } from '../utils/movie';

const fmtRate = (v) => (v ? `${Math.round(v * 1000) / 1000} fps`.replace('.000', '') : '—');
const fmtDur = (s) => {
  if (!s) return '—';
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return m ? `${m}m ${sec}s` : `${sec}s`;
};
const fmtRecorded = (s) => {
  if (!s) return '';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? String(s) : d.toLocaleString();
};
const round1 = (n) => Math.round(n * 10) / 10;

function Row({ label, children }) {
  if (children == null || children === '' || children === '—') return null;
  return (
    <div className="cs-meta-row">
      <span className="cs-meta-key">{label}</span>
      <span className="cs-meta-val">{children}</span>
    </div>
  );
}

const SPEED_PRESETS = [0.25, 0.5, 1, 2, 4];

// Compact clip inspector for the movie editor: technical metadata + lineage
// (from the asset's ffprobe-derived `metadata`/`lineage`) plus a playback-speed
// control. Surfaces the recorded-vs-playback fps so slow-motion footage can be
// retimed to natural speed in one click. `clip` carries tripId/assetId for
// footage sourced from a trip; uploaded movie files fall back to clip fields.
export default function ClipInspector({ clip, onSpeed, title }) {
  const [asset, setAsset] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setAsset(null);
    if (clip?.tripId && clip?.assetId) {
      getAssetDoc(clip.tripId, clip.assetId)
        .then((a) => { if (!cancelled) setAsset(a); })
        .catch(() => {});
    }
    return () => { cancelled = true; };
  }, [clip?.tripId, clip?.assetId]);

  if (!clip) return null;

  const m = asset?.metadata || {};
  const v = m.video || {};
  const a = m.audio || {};
  const cam = m.camera || {};
  const lin = asset?.lineage || {};

  const fpsC = v.fpsContainer || asset?.fpsContainer || 0;
  const fpsA = v.fpsAvg || asset?.fpsAvg || fpsC || clip.fps || 0;
  const capFps = asset?.captureFps || 0;
  // A manually-declared capture rate (for conformed exports with no embedded
  // capture-fps) is stored on the clip as realSpeedX = capture ÷ playback.
  const declaredX = Number(clip.realSpeedX) || 0;
  const derivedX = capFps && fpsA && capFps > fpsA * 1.5 ? capFps / fpsA : 0;
  const realX = declaredX > 1.01 ? declaredX : derivedX;
  // Slow motion: recorded meaningfully faster than it plays back.
  const slowmo = realX > 1.05;
  const naturalSpeed = slowmo ? round1(realX) : 1;
  const recordedFps = capFps || (declaredX > 1.01 ? round1(declaredX * (fpsA || 24)) : 0);
  const sp = clipSpeed(clip);

  const width = v.width || asset?.width || 0;
  const height = v.height || asset?.height || 0;

  return (
    <div className="cs-clipinspect">
      {title && <div className="cs-clipinspect-title">{title}</div>}

      {(capFps || fpsA) ? (
        <div className={`cs-fps-card ${slowmo ? 'is-slowmo' : ''}`}>
          <div className="cs-fps-line">
            <span>Recorded</span>
            <strong>{declaredX > 1.01 && !capFps ? `≈ ${fmtRate(recordedFps)}` : fmtRate(recordedFps || fpsC)}</strong>
          </div>
          <div className="cs-fps-line"><span>Plays at</span><strong>{fmtRate(fpsA)}</strong></div>
          {slowmo ? (
            <div className="cs-fps-note">
              Shot at {declaredX > 1.01 && !capFps ? '≈ ' : ''}{fmtRate(recordedFps)} but plays at {fmtRate(fpsA)} — roughly <b>{naturalSpeed}× slow motion</b>.
              <button
                type="button"
                className="cs-btn cs-btn-primary cs-btn-sm"
                style={{ marginTop: 8 }}
                onClick={() => onSpeed(naturalSpeed)}
                disabled={sp === naturalSpeed}
              >
                {sp === naturalSpeed ? `✓ Natural speed (${naturalSpeed}×)` : `Restore natural speed (${naturalSpeed}×)`}
              </button>
            </div>
          ) : capFps ? (
            <div className="cs-fps-note">Capture and playback rate match — natural speed.</div>
          ) : (
            <div className="cs-fps-note">
              No capture-rate metadata (a conformed export). If this is slow-motion footage, set its true capture rate in the timeline’s <b>⏱ Speed math</b> panel.
            </div>
          )}
        </div>
      ) : (
        <div className="cs-help" style={{ margin: '2px 0 8px' }}>
          {clip.tripId && clip.assetId ? 'Reading clip metadata…' : 'No probe metadata for this source.'}
        </div>
      )}

      <div className="cs-clipinspect-speed">
        <div className="cs-clipinspect-speedhead">
          <span>Playback speed</span>
          <b>{sp}×{sp < 1 ? ' · slow-mo' : sp > 1 ? ' · sped up' : ''}</b>
        </div>
        <div className="cs-clipinspect-speedrow">
          {SPEED_PRESETS.map((p) => (
            <button
              key={p}
              className={`cs-btn cs-btn-sm ${sp === p ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
              onClick={() => onSpeed(p)}
            >
              {p}×
            </button>
          ))}
          <label className="cs-clipinspect-custom">
            custom
            <input
              className="cs-input"
              type="number"
              min="0.1"
              max="10"
              step="0.05"
              value={sp}
              onChange={(e) => {
                const val = Number(e.target.value);
                if (Number.isFinite(val) && val > 0) onSpeed(Math.min(10, Math.max(0.1, val)));
              }}
            />
          </label>
        </div>
        <span className="cs-help" style={{ display: 'block', marginTop: 4 }}>
          Speed bakes in on Publish; the preview retimes live.
        </span>
      </div>

      <button
        type="button"
        className="cs-btn cs-btn-ghost cs-btn-sm"
        style={{ marginTop: 4 }}
        onClick={() => setOpen((o) => !o)}
      >
        {open ? 'Hide details' : 'ⓘ Clip details & lineage'}
      </button>

      {open && (
        <div className="cs-meta-panel" style={{ marginTop: 8 }}>
          <div className="cs-meta-grid">
            <Row label="Resolution">{width ? `${width}×${height}` : null}</Row>
            <Row label="Codec">{[v.codec, v.profile].filter(Boolean).join(' · ')}</Row>
            <Row label="Container">{m.container}</Row>
            <Row label="Source length">{fmtDur(m.durationSec || clip.sourceDurationSec)}</Row>
            <Row label="Container fps">{fpsC ? fmtRate(fpsC) : null}</Row>
            <Row label="Average fps">{fpsA ? fmtRate(fpsA) : null}</Row>
            <Row label="Capture fps">{capFps ? fmtRate(capFps) : null}</Row>
            <Row label="Color">{[v.colorSpace, v.colorPrimaries, v.colorTransfer].filter(Boolean).join(' / ')}</Row>
            <Row label="Bitrate">{m.bitRate ? `${Math.round(m.bitRate / 1000)} kbps` : null}</Row>
            <Row label="Audio">{a.codec ? `${a.codec} · ${a.channels || 0}ch` : null}</Row>
            <Row label="Camera">{[cam.make, cam.model].filter(Boolean).join(' ') || asset?.camera}</Row>
            <Row label="Software">{cam.software}</Row>
            <Row label="Recorded">{fmtRecorded(m.recordedAt || asset?.recordedAt)}</Row>
          </div>
          {(asset?.photographer || lin.origin || lin.contributor || asset?.contributor || lin.originalName) && (
            <>
              <div className="cs-meta-sub">Lineage</div>
              <div className="cs-meta-grid">
                <Row label="Origin">{lin.origin === 'drop' ? 'guest drop link' : lin.origin || 'upload'}</Row>
                <Row label="Via">{lin.via}</Row>
                <Row label="Photographer">{asset?.photographer || lin.contributor || asset?.contributor}</Row>
                <Row label="Original name">{lin.originalName && lin.originalName !== asset?.name ? lin.originalName : null}</Row>
              </div>
            </>
          )}
          {asset === null && clip.tripId && (
            <div className="cs-help" style={{ marginTop: 6 }}>Source asset not found (it may have been moved or deleted).</div>
          )}
        </div>
      )}
    </div>
  );
}
